import React, { createContext, useContext, useState } from 'react';
import { Player } from '../types';
import { connectionManager } from '../lib/supabase/connectionManager';

interface PlayerDeletionContextType {
  deletePlayer: (playerId: string, tournamentId: string) => Promise<void>;
  deleteAllPlayers: (tournamentId: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

const PlayerDeletionContext = createContext<PlayerDeletionContextType | undefined>(undefined);

export function usePlayerDeletion() {
  const context = useContext(PlayerDeletionContext);
  if (!context) {
    throw new Error('usePlayerDeletion must be used within PlayerDeletionProvider');
  }
  return context;
}

interface PlayerDeletionProviderProps {
  children: React.ReactNode;
  onPlayersUpdate: (players: Player[]) => void;
  initialPlayers: Player[];
}

export function PlayerDeletionProvider({ 
  children, 
  onPlayersUpdate,
  initialPlayers 
}: PlayerDeletionProviderProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deletePlayer = async (playerId: string, tournamentId: string) => {
    setIsDeleting(true);
    setError(null);

    try {
      const supabase = connectionManager.getClient();
      if (supabase) {
        const { error: deleteError } = await supabase
          .from('players')
          .delete()
          .eq('id', playerId)
          .eq('tournament_id', tournamentId);

        if (deleteError) throw deleteError;
      }

      // Update local state
      const updatedPlayers = initialPlayers.filter(p => p.id !== playerId);
      onPlayersUpdate(updatedPlayers);
    } catch (error) {
      console.error('Failed to delete player:', error);
      setError('Failed to delete player. Please try again.');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteAllPlayers = async (tournamentId: string) => {
    setIsDeleting(true);
    setError(null);

    try {
      const supabase = connectionManager.getClient();
      if (supabase) {
        const { error: deleteError } = await supabase
          .from('players')
          .delete()
          .eq('tournament_id', tournamentId);

        if (deleteError) throw deleteError;
      }

      // Update local state
      const otherPlayers = initialPlayers.filter(p => p.tournamentId !== tournamentId);
      onPlayersUpdate(otherPlayers);
    } catch (error) {
      console.error('Failed to delete players:', error);
      setError('Failed to delete players. Please try again.');
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PlayerDeletionContext.Provider value={{
      deletePlayer,
      deleteAllPlayers,
      isDeleting,
      error
    }}>
      {children}
    </PlayerDeletionContext.Provider>
  );
}